import { ErrorHandler } from 'components/errors/ErrorHandler';
import { Pagination } from 'components/pagination';
import { LoadingSpinner } from 'components/spinners';
import {
  getUserColumnsByKeys,
  UserTableColumns
} from 'components/tables/users-table/defaults/user-columns';
import { ExpandedBlock } from 'components/tables/users-table/renderers/ExpandedBlock';
import { UserTable } from 'components/tables/users-table/UserTable';
import { Icon, IconName } from 'libs/icons';
import { Month } from 'libs/time';
import { useTranslation } from 'libs/translations';
import _ from 'lodash';
import { useMemo, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import { IUserSearchParamsV2 } from 'services';

import { useAnalyticsContext } from '../contexts/AnalyticsContext';
import { useUsersSearch } from '../hooks/useUsersSearch';

const ITEMS_PER_PAGE = 20;

function booleanOrUndefined(value: string): boolean | undefined {
  if (value.toLowerCase() === 'true') {
    return true;
  }

  if (value.toLowerCase() === 'false') {
    return false;
  }

  return undefined;
}

export function AnalyticsResults() {
  const { t } = useTranslation();
  const { filterParams, searchParams, sortParams } = useAnalyticsContext();
  const isCurrentMonth = Month.create(filterParams.period)?.isTodaysMonth();

  const [pageNumber, setPageNumber] = useState<number>(1);

  const searchQuery: IUserSearchParamsV2 = useMemo(() => {
    setPageNumber(1);

    return {
      ...searchParams,
      ...filterParams,
      ...sortParams,
      byCountry:
        filterParams.byCountry === '' ? undefined : filterParams.byCountry,
      byStructureVolumeTo:
        filterParams.byStructureVolumeTo === Infinity
          ? undefined
          : filterParams.byStructureVolumeTo,
      fitlineClubMembers: booleanOrUndefined(filterParams.fitlineClubMembers),
      newRegistrations: booleanOrUndefined(filterParams.newRegistrations),
      withCheckAssurance: booleanOrUndefined(filterParams.withCheckAssurance),
      withNewQualification: booleanOrUndefined(
        filterParams.withNewQualification
      ),
      withSponsorActivity: booleanOrUndefined(filterParams.withSponsorActivity)
    };
  }, [filterParams, searchParams, sortParams]);

  const {
    data: searchResults,
    isLoading,
    error
  } = useUsersSearch(pageNumber, ITEMS_PER_PAGE, searchQuery);

  const onPageChange = (pageNumber: number) => {
    setPageNumber(pageNumber);
  };

  const tableColumns = useMemo(() => {
    const columnKeys = [
      UserTableColumns.User,
      UserTableColumns.CheckAssurance,
      UserTableColumns.CurrentQualification,
      UserTableColumns.PreviousQualification,
      UserTableColumns.HighestQualification,
      UserTableColumns.FitlineClubMember,
      UserTableColumns.Autoships,
      UserTableColumns.PersonalSales,
      UserTableColumns.SixLevelSales,
      UserTableColumns.SixLevelTrend,
      UserTableColumns.StructureSales,
      UserTableColumns.StructureTrend
    ];

    if (isCurrentMonth) {
      columnKeys.push(UserTableColumns.OpenAutoshipVolume);
    }

    return getUserColumnsByKeys(columnKeys);
  }, [isCurrentMonth]);

  return (
    <section className="bg-white shadow-md rounded-md py-sm">
      {isLoading && (
        <div className="w-full py-md flex items-center justify-center">
          <LoadingSpinner smallSize />
        </div>
      )}

      {!isLoading && error && (
        <div className="w-full min-h-[300px] flex items-center justify-center">
          <ErrorHandler err={error} />
        </div>
      )}

      {!_.isUndefined(searchResults) && (
        <div className="flex justify-end pointer-events-auto">
          <Pagination
            currentPage={searchResults.pageNumber}
            onPageChange={onPageChange}
            totalPages={searchResults.totalPages}
          />
        </div>
      )}

      {!_.isUndefined(searchResults) && (
        <div className="mt-4">
          {searchResults.data.length > 0 && (
            <UserTable
              columns={tableColumns}
              users={searchResults.data}
              renderExpandedBlock={user => (
                <ExpandedBlock userId={user.userId} context="analytics" />
              )}
              tableRowClassName="bg-white border-b-2 border-body"
            />
          )}
          {searchResults.data.length === 0 && (
            <div className="flex flex-col items-center justify-center text-silver gap-sm py-md">
              <Icon name={IconName.Search} size={48} />
              {t('No results found for the selected filters.')}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
